/**
 * Clears the iOS app-icon badge (and the Notification Center list
 * for this app) every time the user brings the app to the
 * foreground. Fenton flagged 2026-09-30: Love Inn's home-screen icon
 * kept a "2" badge indefinitely after the user opened the app +
 * viewed the pushes. Native pattern: apps are responsible for
 * clearing their own badge; iOS won't do it automatically.
 *
 * Implementation notes:
 *   - `PushNotifications.removeAllDeliveredNotifications()` on
 *     iOS also resets the app-icon badge count as a side-effect
 *     (Capacitor push-notifications v6+; see plugin docs). This
 *     avoids adding a native badge-plugin dependency.
 *   - No-op in browsers + when the plugin is unavailable.
 *   - Runs once on mount (covers the cold-launch case where the
 *     app was killed and re-opened) AND on every foreground
 *     transition via @capacitor/app's appStateChange.
 *   - A push arriving while the app is already in foreground WILL
 *     re-set the badge to 1 shortly after this clears it,  that's
 *     correct iOS behaviour (the badge tracks unseen notifications
 *     in Notification Center, and the just-arrived one is unseen).
 *     Not a race with our clear; the clear only ran because the
 *     app was foregrounded before the push arrived.
 */
import { useEffect } from "react";
import { isNativeApp } from "../lib/platform";

export function BadgeClearHook() {
  useEffect(() => {
    if (!isNativeApp()) return;

    let unsub = false;
    let cleanupApp: (() => void) | null = null;

    async function clearBadge() {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        // On iOS this ALSO resets applicationIconBadgeNumber to 0
        // per the @capacitor/push-notifications iOS implementation.
        await PushNotifications.removeAllDeliveredNotifications();
      } catch {
        // Plugin missing / permission never granted / other transient
        // OS failure. Nothing to do; user just sees stale badge.
      }
    }

    void clearBadge();

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const listener = await App.addListener("appStateChange", (state) => {
          if (state.isActive) void clearBadge();
        });
        if (unsub) {
          listener.remove();
          return;
        }
        cleanupApp = () => listener.remove();
      } catch {
        // Capacitor App plugin unavailable; cold-launch clear above
        // already ran.
      }
    })();

    return () => {
      unsub = true;
      cleanupApp?.();
      cleanupApp = null;
    };
  }, []);

  return null;
}
